import { Form, Select } from 'antd';
import {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { RTL_LANGUAGES } from 'constants/translations';
import {
  attachTagsToDoctor,
  createTag,
  detachTagsFromDoctor,
  fetchTags,
  TagRow
} from 'services/api/tags';

import { StyledItem } from './styled';
import useDoctorProfileCache from 'hooks/useDoctorProfileCache';

export type TagsSelectRef = {
  save(): Promise<void>;
};

interface IProps {
  language: string;
  disabled?: boolean;
}

const TagsSelect: ForwardRefRenderFunction<TagsSelectRef, IProps> = (
  { language, disabled },
  ref
) => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  const lang = useMemo(() => searchParams.get('lang') || 'en', [searchParams]);

  const { profile } = useDoctorProfileCache({ id: id as string, lang });

  const [tags, setTags] = useState<TagRow[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchTags(lang);
        if (!cancelled) setTags(res.data?.data ?? []);
      } catch {
        // swallow — dropdown stays empty on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [lang, refreshKey]);

  const [value, setValue] = useState('');

  const hasExactMatch = useMemo(() => {
    if (!value || !tags.length) return false;

    return tags.some(tag => tag.name.toLowerCase() === value.toLowerCase());
  }, [value, tags]);

  const onCreateTag = async () => {
    const name = value;
    setValue('');

    try {
      await createTag({ lang, name });
      setRefreshKey(k => k + 1);
    } catch {
      // swallow — list refetch skipped on failure
    }
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      try {
        const promises: Promise<unknown>[] = [];

        const finalTags: string[] = form.getFieldValue('tags') || [];

        const added = finalTags.filter(tag => !currentTags.includes(tag));
        const doctorId = Number(profile?.id);

        if (added.length) {
          promises.push(attachTagsToDoctor(doctorId, added.map(Number)));
        }

        const deleted = currentTags.filter(tag => !finalTags.includes(tag));

        if (deleted.length) {
          promises.push(detachTagsFromDoctor(doctorId, deleted.map(Number)));
        }

        await Promise.all(promises);

        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    }
  }));

  const currentTags = profile?.tags?.map(item => String(item.id)) || [];

  if (!profile) {
    return null;
  }

  return (
    <Form
      layout="vertical"
      form={form}
      size="large"
      initialValues={{
        tags: currentTags
      }}
    >
      <Form.Item name="tags" label="Tags">
        <Select
          direction={RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'}
          mode="multiple"
          placeholder="Please select or create new"
          style={{ width: '100%' }}
          onSearch={v => setValue(v)}
          searchValue={value}
          filterOption={(input, option) =>
            (option?.label as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          dropdownRender={menu => (
            <>
              {menu}
              {value && !hasExactMatch && (
                <StyledItem
                  onClick={onCreateTag}
                  style={{
                    padding: '8px 12px',
                    borderTop: '1px solid #f0f0f0',
                    margin: 0
                  }}
                >
                  {value} (Create new)
                </StyledItem>
              )}
            </>
          )}
          options={tags.map(item => ({
            label: item.name,
            value: item.id
          }))}
          disabled={disabled}
        />
      </Form.Item>
    </Form>
  );
};

export default forwardRef(TagsSelect);
